import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatDialog } from '@angular/material/dialog';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { AccountService } from '@services/api/account.service';
import {
  AccountListTableComponent,
  AccountViewModel,
} from './account-list-table/account-list-table.component';
import { AccountEditDialogComponent } from './account-edit-dialog/account-edit-dialog.component';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { firstValueFrom } from 'rxjs';
import {
  AccountAddRequest,
  AccountResponse,
  AccountUpdateRequest,
  RoleDto,
} from '@entities/account';
import {
  AccountEditorDialogViewModel,
  EditedAccountViewModel,
} from './account-edit-dialog/account-edit-dialog.model';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute } from '@angular/router';
import { MasterdataService } from '@services/api/masterdata.service';
import { TenantResponse } from '@entities/tenant';
import { TagResponse } from '@entities/tag';

/** ユーザー種別 */
export type UserTypeViewModel = 'system_admin' | 'tenant_admin' | 'tenant_user';

/** テナント */
export type TenantViewModel = {
  /** テナントID */
  tenantId: string;
  /** テナント名 */
  tenantName: string;
};

/** 権限 */
export type RoleViewModel =
  | 'system_admin'
  | 'tenant_admin'
  | 'tenant_editor'
  | 'tenant_viewer';

// デバイスタグのオブジェクト
export type DeviceTag = {
  tagId: string;
  tagName: string;
  color?: string | null;
};

@Component({
  selector: 'app-account-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    MatCardModule,
    AccountListTableComponent,
  ],
  templateUrl: './account-list.component.html',
  styleUrl: './account-list.component.scss',
})
export class AccountListComponent implements OnInit {
  /** ログインユーザー権限 */
  public loggedInUserRole: RoleViewModel | undefined;

  /** ユーザー種別 */
  public selectedUserType: UserTypeViewModel = 'tenant_admin';

  /** アカウント一覧 */
  public accountList: AccountViewModel[] = [];

  /** デバイスタグ一覧 */
  public deviceTagList: DeviceTag[] = [];

  /** テナント一覧 */
  public tenantList: TenantViewModel[] = [];

  /** 所属テナント */
  public currentTenant: TenantViewModel | undefined;

  /** ログインユーザーのユーザーID */
  private loginUserId: string = '';

  constructor(
    private accountService: AccountService,
    private masterdataService: MasterdataService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    // ログインユーザーの権限を取得する
    const loggedInUser: AccountResponse =
      this.route.snapshot.data['loggedInUser'];
    this.loggedInUserRole = loggedInUser.tenant.roleId;
    this.currentTenant = {
      tenantId: loggedInUser.tenant.tenantId,
      tenantName: loggedInUser.tenant.tenantName,
    };
    this.loginUserId = loggedInUser.email;

    /** 選択しているユーザー種別を設定 */
    this.selectedUserType =
      this.loggedInUserRole === 'system_admin'
        ? 'system_admin'
        : 'tenant_admin';

    const dataType = ['tenant', 'tag'];
    this.masterdataService.find(dataType).subscribe({
      next: (masterdata) => {
        // マスターデータを取得する
        const tenantList = masterdata['tenant'] as TenantResponse[];
        this.tenantList = tenantList.map((tenant) => ({
          tenantId: tenant.tenantId,
          tenantName: tenant.tenantName,
        }));

        const tagList = masterdata['tag'] as TagResponse[];
        for (const tag of tagList) {
          const tag_dict: DeviceTag = {
            tagId: tag.tagId,
            tagName: tag.tagName,
            color: tag.color,
          };
          this.deviceTagList.push(tag_dict);
        }

        // アカウント一覧を取得する
        this._initAccountList();
      },
    });
  }

  /**
   * アカウント追加/編集ダイアログを表示する
   * @param account アカウント
   */
  public async openAccountEditDialog(account?: AccountViewModel) {
    const options: AccountEditorDialogViewModel = {
      loggedInUserRole: this.loggedInUserRole as RoleViewModel,
      userType: this.selectedUserType,
      updating: false,
      deviceTagList: this.deviceTagList,
      tenantList: this.tenantList,
      currentTenant: this.currentTenant,
    };

    if (account) {
      options.updating = true;
      options.target = {
        email: account.email,
        familyName: account.familyName,
        givenName: account.givenName,
        familyNameKana: account.familyNameKana,
        givenNameKana: account.givenNameKana,
        mfa: account.mfaRequired,
        tenantId: account.tenant.tenantId,
        configurable: account.configurable,
        deviceTagList: account.deviceTagList,
      };
    }

    // 顧客ユーザーの場合、項目が多いため、広く表示する
    const dialogWidth =
      this.selectedUserType === 'tenant_user' ? '800px' : '700px';
    const editedAccount: EditedAccountViewModel = await firstValueFrom(
      this.dialog
        .open(AccountEditDialogComponent, {
          width: dialogWidth,
          data: options,
        })
        .afterClosed(),
    );

    // ダイアログ外をクリックしてダイアログが閉じられたときは更新/追加処理をしない
    if (!editedAccount) {
      return;
    }

    let role: RoleDto;
    if (this.selectedUserType === 'tenant_user') {
      role = editedAccount.configurable ? 'tenant_editor' : 'tenant_viewer';
    } else {
      role = this.selectedUserType;
    }

    if (options.updating) {
      // アカウント更新処理
      const updatedAccountRequest: AccountUpdateRequest = {
        userId: editedAccount.email,
        email: editedAccount.email,
        familyName: editedAccount.familyName,
        givenName: editedAccount.givenName,
        familyNameKana: editedAccount.familyNameKana,
        givenNameKana: editedAccount.givenNameKana,
        roleId: role,
        device_tag_list: editedAccount.deviceTagList,
        mfaRequired: editedAccount.mfa,
        tenant: {
          tenantId: editedAccount.tenantId,
          roleId: role,
        },
      };
      this.accountService
        .update(this.selectedUserType, updatedAccountRequest)
        .subscribe({
          next: () => {
            // アカウント一覧を再取得する
            this._initAccountList();
          },
          error: (error) => {
            let message = '';
            if (
              error.response.status == 400 &&
              error.response.error.detail.type == 'NotFoundWebException'
            ) {
              message =
                '他のユーザーによってデータが変更されたため、更新に失敗しました。最新のデータを取得して再度試してください。';
            } else if (
              error.response.error.detail.type == 'AlreadyExistsWebException'
            ) {
              message = `メールアドレスが重複しているため、更新に失敗しました。\n別の名前でもう一度お試しください。`;
            } else {
              message =
                'アカウントの変更に失敗しました。\nしばらくしてからもう一度お試しください。';
            }
            const alertDialogOptions: AlertDialogOption = {
              type: 'error',
              message: message,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          },
        });
    } else {
      // アカウント追加処理
      const addedAccountRequest: AccountAddRequest = {
        userId: editedAccount.email,
        email: editedAccount.email,
        familyName: editedAccount.familyName,
        givenName: editedAccount.givenName,
        familyNameKana: editedAccount.familyNameKana,
        givenNameKana: editedAccount.givenNameKana,
        device_tag_list: editedAccount.deviceTagList,
        mfaRequired: editedAccount.mfa,
        tenant: {
          tenantId: editedAccount.tenantId,
          roleId: role,
        },
      };
      this.accountService
        .add(this.selectedUserType, addedAccountRequest)
        .subscribe({
          next: () => {
            // アカウント一覧を再取得する
            this._initAccountList();
          },
          error: (error) => {
            let message = '';
            if (
              error.response.error.detail.type == 'AlreadyExistsWebException'
            ) {
              message = `メールアドレスが重複しているため、追加に失敗しました。\n別の名前でもう一度お試しください。`;
            } else {
              message =
                'アカウントの追加に失敗しました。\nしばらくしてからもう一度お試しください。';
            }
            const alertDialogOptions: AlertDialogOption = {
              type: 'error',
              message: message,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          },
        });
    }
  }

  /**
   * アカウントを削除する
   * @param accountList アカウント一覧
   */
  public async deleteAccount(accountList: AccountViewModel[]) {
    const userIdList = accountList.map((account) => account.userId);
    if (userIdList.includes(this.loginUserId)) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message:
          '自身は削除できません。別アカウントでログイン後に削除してください。',
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
      return;
    }
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `選択したアカウント ${accountList.length} 件を削除してよろしいですか？`,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    this.accountService.delete(this.selectedUserType, userIdList).subscribe({
      next: () => {
        // アカウント一覧を再取得する
        this._initAccountList();
      },
      error: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: `アカウント削除に失敗しました。\nしばらくしてからもう一度お試しください。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  /**
   * テーブルが閲覧のみか
   */
  public get tableReadonly(): boolean {
    return (
      this.loggedInUserRole === 'system_admin' &&
      this.selectedUserType === 'tenant_user'
    );
  }

  private _initAccountList() {
    this.accountService.findAll().subscribe({
      next: (accountList) => {
        this.accountList = accountList.map((account) => {
          const role = account.tenant?.roleId;
          let configurable = false;
          let userType: UserTypeViewModel = 'tenant_user';
          if (role === 'tenant_viewer' || !role) {
            userType = 'tenant_user';
            configurable = false;
          } else if (role === 'tenant_editor') {
            userType = 'tenant_user';
            configurable = true;
          } else {
            userType = role;
          }

          const tenant: TenantViewModel = {
            tenantId: account.tenant?.tenantId,
            tenantName: account.tenant?.tenantName,
          };

          return {
            userId: account.userId,
            email: account.email,
            userName: account.userName,
            familyName: account.familyName,
            givenName: account.givenName,
            familyNameKana: account.familyNameKana,
            givenNameKana: account.givenNameKana,
            emailStatus: account.emailStatus,
            mfaRequired: account.mfaRequired,
            mfaStatus: account.mfaStatus,
            lastLoginAt: account.lastLoginAt,
            deviceTagList: account.deviceTagList,
            tenant: tenant,
            userType: userType,
            configurable,
          };
        });
      },
    });
  }
}
